/**
 * What a reply did on the person's machine, through a connector, on the
 * chat's computer, in Assist itself or as code run in the page, one row a
 * call, drawn like the lookups above it. A row opens to what the call was
 * given and what came back. More than one call in a round sit under one line
 * ("Ran 3 commands"), open while any of them is still going and closed once
 * all are done, until the person opens or closes it.
 *
 * A reply that stopped to wait for the person ends with the approval block:
 * what it wants to do, every field of its arguments, and Allow or Deny — or,
 * stopped at its last round of calls, with Continue or Stop. Agents to start
 * are named one a line, each opening to its task, over Start or Deny. A call the
 * desktop app runs is approved by the app itself, never here: a page cannot
 * approve what the machine does.
 */

import { useMemo, useState, type CSSProperties } from 'react';
import { fileUrl, galleryVideoUrl, type PendingCall, type ToolRun } from '../api';
import { ds } from '../ds';
import { useApp } from '../state';
import { agentPlan, startHeading, type AgentPlan } from './agentsText';
import { argumentFields } from './approvalText';
import { shortName } from './composer/ModelMenu';
import { highlight } from './highlight';
import { groupLabel, inputLanguage, isComputerCall, settled, stateText } from './toolCallsText';
import { ArtifactTile } from './ArtifactTile';
import { Ph } from './ui';

const row: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, minHeight: 28, minWidth: 0, fontSize: 13.5 };
const oneLine: CSSProperties = { overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' };
/** What a reply waiting for the person asks in. */
const waitingCard: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 'var(--radius-app-card)',
  border: '1px solid var(--border-strong)',
  background: 'var(--surface-card)'
};

const AGENT_TOOLS = new Set(['start_agents', 'message_agent', 'wait_agents', 'stop_agents']);

const iconOf = (c: ToolRun): string =>
  c.tool === 'research'
    ? 'magnifying-glass'
    : c.tool === 'generate_video'
    ? 'video-camera'
    : c.tool === 'create_artifact' || c.tool === 'update_artifact'
    ? 'paint-brush'
    : AGENT_TOOLS.has(c.tool)
    ? 'users-three'
    : isComputerCall(c)
    ? 'cursor-click'
    : c.where === 'desktop'
    ? 'desktop'
    : c.where === 'sandbox'
      ? 'terminal-window'
      : c.where === 'assist'
        ? 'sparkle'
        : c.where === 'page'
          ? 'code'
          : 'plugs-connected';

/** What a call was given, coloured, and what came back. */
function CallDetail({ call }: { call: ToolRun }) {
  const lang = inputLanguage(call);
  const runs = useMemo(() => (call.input ? highlight(call.input, lang) : []), [call.input, lang]);
  const box: CSSProperties = {
    margin: 0,
    padding: '8px 12px',
    borderRadius: 'var(--radius-sm)',
    fontFamily: 'var(--font-mono)',
    fontSize: 12.5,
    lineHeight: 1.55,
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere'
  };
  return (
    <div className="selectable" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '2px 0 8px 23px', minWidth: 0 }}>
      {call.input ? (
        <pre style={{ ...box, background: 'var(--surface-code)', color: 'var(--slate-200)' }}>
          {call.tool === 'run_command' ? <span style={{ color: 'var(--slate-500)' }}>$ </span> : null}
          {runs.map((r, i) =>
            r.colour ? (
              <span key={i} style={{ color: r.colour }}>
                {r.text}
              </span>
            ) : (
              r.text
            )
          )}
        </pre>
      ) : null}
      {call.output ? (
        <pre
          style={{
            ...box,
            maxHeight: 280,
            overflowY: 'auto',
            background: 'var(--app-panel-sunken)',
            color: 'var(--text-muted)'
          }}
        >
          {call.output}
        </pre>
      ) : null}
      {call.files?.map((f) => (
        <a key={f.fileId} href={fileUrl(f.fileId)} target="_blank" rel="noopener noreferrer" title={f.name} style={{ alignSelf: 'flex-start' }}>
          <img
            src={fileUrl(f.fileId)}
            alt={f.name}
            loading="lazy"
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: 320,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--app-panel-sunken)'
            }}
          />
        </a>
      ))}
    </div>
  );
}

/** One call: what it is and how it stands, opening to its detail when it
 *  kept any. */
function CallRow({ call }: { call: ToolRun }) {
  const [open, setOpen] = useState(false);
  const opens = !!(call.input || call.output || call.files?.length);
  const head = (
    <>
      <Ph
        name={iconOf(call)}
        size={15}
        className={call.state === 'running' || (call.state === 'waiting' && (call.where === 'page' || call.where === 'assist')) ? 'pulse' : undefined}
        style={{ flex: 'none' }}
      />
      <span style={{ ...oneLine, flex: '0 1 auto', color: 'var(--text-body)' }}>{call.label}</span>
      <span style={{ ...oneLine, flex: 'none', fontSize: 12.5, color: call.state === 'failed' ? 'var(--danger-600)' : 'var(--text-faint)' }}>
        {stateText(call)}
      </span>
      {opens ? <Ph name={open ? 'caret-up' : 'caret-down'} size={12} style={{ flex: 'none', color: 'var(--text-faint)' }} /> : null}
    </>
  );
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {opens ? (
        <button
          type="button"
          className="bare hov-strong"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          style={{ ...row, width: '100%', color: 'var(--text-muted)' }}
        >
          {head}
        </button>
      ) : (
        <div style={{ ...row, color: 'var(--text-muted)' }}>{head}</div>
      )}
      {open ? <CallDetail call={call} /> : null}
    </div>
  );
}

/** A clip a call made, played where the reply is. */
function CallVideo({ video }: { video: NonNullable<ToolRun['video']> }) {
  return (
    <video
      src={galleryVideoUrl(video.id)}
      controls
      playsInline
      preload="metadata"
      style={{
        display: 'block',
        width: '100%',
        maxWidth: video.width >= video.height ? 560 : 300,
        aspectRatio: `${video.width} / ${video.height}`,
        borderRadius: 'var(--radius-app-card)',
        background: 'var(--app-panel-sunken)'
      }}
    />
  );
}

export function ToolCalls({ calls }: { calls: readonly ToolRun[] }) {
  const made = calls.flatMap((c) => (c.video ? [c.video] : []));
  // An artifact saved is its tile, in place of its row.
  const tiles = calls.filter((c) => c.artifact);
  const rows = tiles.length ? calls.filter((c) => !c.artifact) : calls;
  if (!made.length && !tiles.length) return <CallList calls={calls} />;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
      {rows.length ? <CallList calls={rows} /> : null}
      {made.map((v) => (
        <CallVideo key={v.id} video={v} />
      ))}
      {tiles.map((c) => (
        <ArtifactTile key={c.id} artifact={c.artifact!} />
      ))}
    </div>
  );
}

function CallList({ calls }: { calls: readonly ToolRun[] }) {
  const [open, setOpen] = useState<boolean | null>(null);
  if (calls.length === 1) return <CallRow call={calls[0]!} />;
  // Open while any call is still going, so its state shows; closed once all
  // are done, unless the person says otherwise.
  const shown = open ?? !calls.every(settled);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <button
        type="button"
        className="bare hov-strong"
        aria-expanded={shown}
        onClick={() => setOpen(!shown)}
        style={{ ...row, color: 'var(--text-muted)' }}
      >
        <span style={oneLine}>{groupLabel(calls)}</span>
        <Ph name={shown ? 'caret-up' : 'caret-down'} size={12} style={{ flex: 'none', color: 'var(--text-faint)' }} />
      </button>
      {shown ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: '2px 10px',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-app-card)'
          }}
        >
          {calls.map((c) => (
            <CallRow key={c.id} call={c} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type Decision = 'allowed' | 'allowed_chat' | 'denied';

/** One agent a card would start: its title, and its task a click away. Its
 *  model only where it is not the reply's own. */
function PlannedAgent({ agent, model }: { agent: AgentPlan; model: string | null }) {
  const [open, setOpen] = useState(false);
  const { catalogue } = useApp();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <button
        type="button"
        className="bare hov-strong"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{ ...row, width: '100%', color: 'var(--text-body)' }}
      >
        <span style={{ ...oneLine, flex: '0 1 auto' }}>{agent.title}</span>
        {agent.model !== model ? (
          <span style={{ ...oneLine, flex: 'none', fontSize: 12.5, color: 'var(--text-faint)' }}>{shortName(agent.model, catalogue?.models)}</span>
        ) : null}
        <Ph name={open ? 'caret-up' : 'caret-down'} size={12} style={{ flex: 'none', color: 'var(--text-faint)' }} />
      </button>
      {open ? (
        <span
          className="selectable"
          style={{ padding: '0 0 6px', fontSize: 13, lineHeight: 1.5, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}
        >
          {agent.task}
        </span>
      ) : null}
    </div>
  );
}

/** start_agents, waiting: how many, and each one. */
function StartAgents({ plan, model }: { plan: AgentPlan[]; model: string | null }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
        <Ph name="users-three" size={15} style={{ flex: 'none' }} />
        <span style={oneLine}>{startHeading(plan.length)}</span>
      </span>
      {plan.map((a, i) => (
        <PlannedAgent key={i} agent={a} model={model} />
      ))}
    </div>
  );
}

export function ApprovalBlock({
  calls,
  model,
  onDecide
}: {
  /** The calls waiting for the person, not the desktop app. */
  calls: readonly PendingCall[];
  /** The reply's model, which agents use unless they name another. */
  model: string | null;
  onDecide: (decision: Decision) => void;
}) {
  const { Button } = ds();
  const plans = calls.map((c) => (c.name === 'start_agents' ? agentPlan(c.arguments) : null));
  const starts = plans.every(Boolean);
  return (
    <div
      role="group"
      aria-label="Waiting for you"
      style={waitingCard}
    >
      {calls.map((c, i) => {
        const plan = plans[i];
        if (plan) return <StartAgents key={c.id} plan={plan} model={model} />;
        return (
          <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>
              <Ph name="plugs-connected" size={15} style={{ flex: 'none' }} />
              <span style={oneLine}>{c.label}</span>
            </span>
            {c.arguments && c.arguments !== '{}' ? (
              <code
                className="selectable"
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere'
                }}
              >
                {argumentFields(c.arguments)?.map(([key, value], i) => (
                  <span key={i} style={{ display: 'block', maxHeight: 96, overflow: 'auto' }}>
                    <span style={{ color: 'var(--text-body)' }}>{key}:</span> {value}
                  </span>
                )) ?? c.arguments}
              </code>
            ) : null}
          </div>
        );
      })}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Button variant="primary" density="app" size="sm" onClick={() => onDecide('allowed')}>
          {starts ? 'Start' : 'Allow'}
        </Button>
        <Button variant="ghost" density="app" size="sm" onClick={() => onDecide('denied')}>
          Deny
        </Button>
      </div>
    </div>
  );
}

/** A reply that stopped after `steps` rounds of calls: it goes on, or ends
 *  there. Allowed and denied, as an approval is. */
export function StepLimitBlock({ steps, onDecide }: { steps: number; onDecide: (decision: Decision) => void }) {
  const { Button } = ds();
  return (
    <div
      role="group"
      aria-label="Waiting for you"
      style={waitingCard}
    >
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-strong)' }}>Still working after {steps} steps.</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        <Button variant="primary" density="app" size="sm" onClick={() => onDecide('allowed')}>
          Continue
        </Button>
        <Button variant="ghost" density="app" size="sm" onClick={() => onDecide('denied')}>
          Stop
        </Button>
      </div>
    </div>
  );
}
